import ItiButton from "@/iti-core-react/components/ItiButton/ItiButton";
import ItiPopUpComponent from "@/iti-core-react/components/ItiPopUp/ItiPopUp";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ConditionDto } from "@/shared/dtos/Centralized/condition-dto";
import { UserRegistrationConditionService } from "@/shared/services/userRegistrationConditionService";
import { useDevice } from "@/utils/media-query";
import List from "devextreme-react/list";
import { useCallback, useEffect, useRef, useState } from "react";
import "./ShowLegalConditionsPopup.scss";
import { buildHtmlDocument } from "./utils/htmlDocumentBuilder";

interface ShowLegalConditionsPopupProps {
	readonly conditionClauseId?: string;
	readonly inlineHtml?: string;
	readonly requireAcceptance?: boolean;
	readonly title?: string;
	readonly subtitle?: string;
	readonly onHide?: () => void;
	readonly onAccept?: () => void;
	readonly onConditionsLoaded?: (terms: ConditionDto) => void;
}

export default function ShowLegalConditionsPopup({
	conditionClauseId,
	inlineHtml,
	requireAcceptance,
	title,
	subtitle,
	onHide,
	onAccept,
	onConditionsLoaded,
}: ShowLegalConditionsPopupProps) {
	const { t } = useLanguage();

	const [conditionClause, setConditionClause] = useState(new ConditionDto());
	const [isAllClausesAccepted, setIsAllClausesAccepted] = useState(false);
	const [checkDisabled, setCheckDisabled] = useState(true);
	const [selectedItemKeys, setSelectedItemKeys] = useState<any[]>([]);
	const [frameHtml, setFrameHtml] = useState<string>("");
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const INFORMATIVE_CONDITIONS = new Set(["LEGAL_NOTICE", "PRIVACY_COOKIES"]);
	const isInformative = conditionClauseId
		? INFORMATIVE_CONDITIONS.has(conditionClauseId)
		: false;

	// Validación adicional: si no hay cláusulas, también es informativa (Pendiente de confirmación)
	// const hasClauses = (conditionClause.conditionClauseList?.length ?? 0) > 0;

	const showAcceptance =
		typeof requireAcceptance === "boolean"
			? requireAcceptance
			: !!conditionClauseId && !isInformative;

	const { isDesktop } = useDevice();

	const handleIframeLoad = useCallback(() => {
		const iframe = iframeRef.current;
		if (iframe?.contentDocument) {
			iframe.contentDocument.addEventListener("contextmenu", (e) => {
				e.preventDefault();
			});
		}
	}, []);

	const handleContextMenu = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		return false;
	}, []);

	const onSelectedItemKeysChange = useCallback(
		(e: any) => {
			if (e.name === "selectedItemKeys") {
				setSelectedItemKeys(e.value);
				const options = conditionClause.conditionClauseList;
				setIsAllClausesAccepted(
					(e.value?.length ?? 0) === (options?.length ?? 0),
				);
			}
		},
		[conditionClause.conditionClauseList],
	);

	useEffect(() => {
		if (inlineHtml?.trim()) {
			setFrameHtml(buildHtmlDocument(inlineHtml));
			setIsAllClausesAccepted(true);
			setCheckDisabled(false);
			return;
		}

		if (conditionClauseId) {
			UserRegistrationConditionService.getConditionClauses(
				conditionClauseId,
			).then((conditions) => {
				setConditionClause(conditions);
				setIsAllClausesAccepted(false);
				setCheckDisabled(true);
				setFrameHtml(buildHtmlDocument(conditions.text ?? ""));
				onConditionsLoaded?.(conditions);
			});
		} else {
			setFrameHtml("");
		}
	}, [inlineHtml, conditionClauseId, onConditionsLoaded]);

	useEffect(() => {
		if (!showAcceptance) return;
		const iframe = iframeRef.current;
		if (!iframe) return;

		const onLoad = () => {
			try {
				const cw = iframe.contentWindow;
				if (!cw) return;

				const checkScrollPosition = () => {
					const el = cw.document?.documentElement;
					if (!el) return;
					const bottom =
						Math.ceil(el.scrollTop) + el.clientHeight >= el.scrollHeight;
					setCheckDisabled(!bottom);
				};

				// Verificar si al abrirse el popup ya hay o no scroll
				const el = cw.document?.documentElement;
				if (el) {
					const hasScroll = el.scrollHeight > el.clientHeight;
					if (hasScroll) {
						checkScrollPosition();
					} else {
						// Si no hay scroll, habilitar los checks inmediatamente
						setCheckDisabled(false);
					}
				}

				cw.onscroll = checkScrollPosition;
			} catch {
				setCheckDisabled(false);
			}
		};

		iframe.addEventListener("load", onLoad);
		return () => {
			try {
				const cw = iframe.contentWindow;
				if (cw) cw.onscroll = null;
			} catch {}
			iframe.removeEventListener("load", onLoad);
		};
	}, [showAcceptance, frameHtml]);

	return (
		<ItiPopUpComponent
			title={t("terms-conditions.title")}
			visible={true}
			width={isDesktop ? "1024px" : "95%"}
			height="auto"
			onHide={onHide}
		>
			<div className="legal-condition-form" onContextMenu={handleContextMenu}>
				<div>
					<h5>{title ?? conditionClause.name}</h5>
					<h6>{subtitle ?? conditionClause.description}</h6>
				</div>

				<iframe
					className="mt-4 contract-item"
					ref={iframeRef}
					title="text"
					width="100%"
					height={window.innerHeight * 0.5}
					srcDoc={frameHtml}
					key={frameHtml ? frameHtml.length : 0}
					onLoad={handleIframeLoad}
				/>

				{showAcceptance && (
					<>
						<div className={"list-condition"}>
							<List
								keyExpr={"clauseId"}
								displayExpr={"clauseText"}
								dataSource={conditionClause.conditionClauseList}
								showSelectionControls={true}
								selectedItemKeys={selectedItemKeys}
								disabled={checkDisabled}
								selectionMode="multiple"
								onOptionChanged={onSelectedItemKeysChange}
							/>
						</div>
						<div>
							{isAllClausesAccepted && (
								<div className="requestContainer">
									<ItiButton
										className="mt-3"
										type="default"
										icon="mdi mdi-check-circle catalogue-link"
										text={t("terms-conditions.accept-continue")}
										onClick={onAccept}
									/>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</ItiPopUpComponent>
	);
}
